import type { DocNode } from '@atlaskit/adf-schema';
import { traverse } from '@atlaskit/adf-utils/traverse';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import FeatureGates from '@atlaskit/feature-gate-js-client';

// Regex patterns for AVP charts that should always render as embed
const avpRovoSkillsChartPattern =
	/^https?:\/\/(?:www\.)?avp-rovo-skills\.[-\w]+\.frontend\.public\.atl-paas\.net\/v1\/chart/;
const avpChartsPattern = /^https?:\/\/(?:www\.)?.*?\/avpviz\/c\/[^/]+/;

/**
 * Checks if a URL is an AVP chart that should remain as embed
 */
const isAvpChartUrl = (url: string | undefined): boolean => {
	if (!url) {
		return false;
	}
	return avpRovoSkillsChartPattern.test(url) || avpChartsPattern.test(url);
};

/**
 * Checks if a paragraph node ends with a hardBreak
 */
const endsWithHardBreak = (node: ADFEntity): boolean => {
	if (node.type !== 'paragraph' || !node.content?.length) {
		return false;
	}
	const lastChild = node.content[node.content.length - 1];
	return lastChild?.type === 'hardBreak';
};

/**
 * Converts an embedCard node to an inlineCard node
 */
const convertToInlineCard = (embedCard: ADFEntity): ADFEntity => ({
	type: 'inlineCard',
	attrs: {
		url: embedCard.attrs?.url,
	},
});

/**
 * Strips the non-ADF-schema `isStandalone` marker attribute from an embedCard
 * before it is returned to downstream consumers (renderer, serialisers, persistence).
 * The marker is only used internally by this plugin to detect standalone-line URLs.
 */
const stripStandaloneMarker = (node: ADFEntity): ADFEntity => {
	if (!node.attrs || !('isStandalone' in node.attrs)) {
		return node;
	}
	const { isStandalone: _isStandalone, ...restAttrs } = node.attrs as Record<string, unknown>;
	return { ...node, attrs: restAttrs };
};

/**
 * ADF plugin that converts embedCard nodes to inlineCard nodes
 * when they are not preceded by a hardBreak (line break).
 *
 * This prevents embed cards from breaking the flow of text when
 * they appear in the middle of content.
 */
export const adfSmartlinksPlugin = (adf: DocNode): DocNode => {
	if (!adf.content || adf.content.length === 0) {
		return adf;
	}

	// CRCS-5223: When the new-3P experiment is on, all embedCards at doc level
	// are intentional block-level cards produced from standalone-line URLs.
	// The downgrade-and-merge path must be skipped entirely so that their
	// position in the document is never changed.
	const experimentEnabled = FeatureGates.getExperimentValue(
		'rovo_chat_smartlink_new_third_parties',
		'isEnabled',
		false,
	);

	// Track the last paragraph we've seen at doc level
	let lastParagraph: ADFEntity | null = null;

	const result = traverse(adf as ADFEntity, {
		paragraph: (node, parent) => {
			// Only track paragraphs at doc level to avoid merging into nested paragraphs
			if (parent.node?.type === 'doc') {
				lastParagraph = node;
			}
			return node;
		},
		embedCard: (node) => {
			const url = node.attrs?.url as string | undefined;

			// AVP charts should always remain as embed cards
			if (isAvpChartUrl(url)) {
				return stripStandaloneMarker(node);
			}

			// Check if we have a previous paragraph that ends with hardBreak
			if (lastParagraph && endsWithHardBreak(lastParagraph)) {
				// Remove the trailing hardBreak since embed will be a block element
				if (lastParagraph.content) {
					lastParagraph.content = lastParagraph.content.slice(0, -1);
				}
				// Keep the embedCard as-is (without leaking the internal marker)
				return stripStandaloneMarker(node);
			}

			// Skip the downgrade-and-merge for embedCards that were produced from a standalone-line URL (marked
			// with isStandalone by the md-plugin-smartlink tokenizer).
			// Mid-sentence embeds (where text precedes the URL on the same line) must still be
			// downgraded and merged so the sentence reads naturally as an inlineCard.
			// When the experiment is off, the original behaviour is fully preserved.
			if (experimentEnabled && node.attrs?.isStandalone === 'true') {
				return stripStandaloneMarker(node);
			}

			if (lastParagraph) {
				// No hardBreak - convert to inline and append to previous paragraph
				const inlineCard = convertToInlineCard(node);
				// Add a space before the inline card to preserve text separation
				const spaceNode: ADFEntity = {
					type: 'text',
					text: ' ',
				};
				if (lastParagraph.content) {
					lastParagraph.content.push(spaceNode, inlineCard);
				} else {
					lastParagraph.content = [spaceNode, inlineCard];
				}
				// Drop the embedCard from the document
				return false;
			}

			// No previous paragraph — keep the embedCard in its original position
			// (without leaking the internal marker).
			return stripStandaloneMarker(node);
		},
	});

	return (result || adf) as DocNode;
};
